import {
  AddressFieldValue,
  DonationFlowCustomizationFormData,
} from "@components/CustomizeDonationExperience/types";
import Big from "big.js";

import {
  CustomFieldType,
  DonationFlowCustomizationData,
} from "@every.org/common/src/codecs/donationFlowCustomization";
import { safeIntCodec } from "@every.org/common/src/codecs/number";
import {
  Currency,
  DonationFlowPaymentOption,
  DonationFrequency,
  DonationFrequencyToUserFacingTextMap,
} from "@every.org/common/src/entity/types";
import { UTM_QUERY_PARAM } from "@every.org/common/src/helpers/analytics";
import {
  DonateModalUrlParams,
  getDonateRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { minimumDenominationAmountToCurrencyValue } from "@every.org/common/src/helpers/currency";
import { DEFAULT_BOUNDS_FOR_DONATION_VALUE } from "@every.org/common/src/helpers/donationValue";
import { removeUndefinedOrNullValues } from "@every.org/common/src/helpers/objectUtilities";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";

export const CONFIGURE_BUTTON_FORM_DEFAULT_VALUES: DonationFlowCustomizationFormData =
  {
    paymentOptions: Object.values(DonationFlowPaymentOption).join(","),
    amountRadio: "can-choose",
    frequency: "can-choose",
    minAmount: "10",
    suggestedAmounts: "40,100,250",
    donorContactInfo: "optional",
    amount: "",
    themeColor: "",
    designation: "",
    address: "show",
    addressRequired: "optional",
    customFields: null,
    customHeading: "",
    customPlaceholder: "",
    customFieldRequired: "optional",
    dropdownOptions: "",
  };

export const CUSTOM_HEADING_MAX_LENGTH = 100;
export const CUSTOM_PLACEHOLDER_MAX_LENGTH = 100;
export const DROPDOWN_OPTIONS_MAX_LENGTH = 1000;

export const FREQUENCY_OPTIONS: ReadonlyArray<{
  label: string;
  dataTname: string;
  value: DonationFlowCustomizationFormData["frequency"];
}> = [
  {
    label: "Donor can choose",
    dataTname: "frequency-can-choose",
    value: "can-choose",
  },
  {
    label: DonationFrequencyToUserFacingTextMap[DonationFrequency.ONCE],
    dataTname: "frequency-one-time",
    value: DonationFrequency.ONCE,
  },
  {
    label: DonationFrequencyToUserFacingTextMap[DonationFrequency.MONTHLY],
    dataTname: "frequency-monthly",
    value: DonationFrequency.MONTHLY,
  },
  {
    label: DonationFrequencyToUserFacingTextMap[DonationFrequency.YEARLY],
    dataTname: "frequency-yearly",
    value: DonationFrequency.YEARLY,
  },
];

export function isCustomizationFrequencyOption(
  value: string | null | undefined
): value is DonationFlowCustomizationFormData["frequency"] {
  return FREQUENCY_OPTIONS.some((option) => option.value === value);
}

const DEFAULT_CUSTOMIZATION_DATA = formDataToCustomizationData(
  CONFIGURE_BUTTON_FORM_DEFAULT_VALUES
);

export function commaSeparatedValuesToString(values: string[]) {
  return values.join(",");
}

export function commaSeparatedValuesFromString(valuesString: string) {
  return valuesString.split(",");
}

interface MinMaxValidationArgs {
  value: string;
  key: keyof DonationFlowCustomizationFormData;
  nonprofit: ContextNonprofit;
  setError: (
    field: keyof DonationFlowCustomizationFormData,
    error: { message: string }
  ) => void;
}

function minMaxValidation({
  value,
  key,
  nonprofit,
  setError,
}: MinMaxValidationArgs) {
  const currency = Currency.USD;
  const boundsForCurrency = DEFAULT_BOUNDS_FOR_DONATION_VALUE[currency];
  const minAmount = (
    nonprofit.metadata?.minDonationValue ||
    minimumDenominationAmountToCurrencyValue({
      currency,
      amount: boundsForCurrency.min,
    })
  ).amount;
  const maxAmount = minimumDenominationAmountToCurrencyValue({
    currency,
    amount: boundsForCurrency.max,
  }).amount;

  const amount = new Big(value);
  if (amount.lt(minAmount)) {
    setError(key, {
      message: `The minimum donation amount is $${minAmount.toString()}`,
    });
    return false;
  }
  if (amount.gt(maxAmount)) {
    setError(key, {
      message: `The maximum donation amount is $${maxAmount.toString()}`,
    });
    return false;
  }
  return true;
}

export function validateForm(
  values: DonationFlowCustomizationFormData,
  nonprofit: ContextNonprofit,
  setError: (
    field: keyof DonationFlowCustomizationFormData,
    error: { message: string }
  ) => void
) {
  if (values.amountRadio === "fixed") {
    if (!values.amount) {
      setError("amount", {
        message: "Please enter a donation amount",
      });
      return false;
    } else if (
      !minMaxValidation({
        value: values.amount,
        key: "amount",
        nonprofit,
        setError,
      })
    ) {
      return false;
    }
  }

  if (values.minAmount) {
    if (
      !minMaxValidation({
        value: values.minAmount,
        key: "minAmount",
        nonprofit,
        setError,
      })
    ) {
      return false;
    }
  }

  const suggestedAmounts = commaSeparatedValuesFromString(
    values.suggestedAmounts
  ).filter((amount) => amount);

  for (const amount of suggestedAmounts) {
    if (
      !minMaxValidation({
        value: amount,
        key: "suggestedAmounts",
        nonprofit,
        setError,
      })
    ) {
      return false;
    }
  }

  if (values.themeColor && !/^#[0-9a-f]{6}$/i.test(values.themeColor)) {
    setError("themeColor", {
      message: "Please enter a valid hex color",
    });
    return false;
  }

  if (values.customFields && !values.customHeading.trim()) {
    setError("customHeading", {
      message: "Please enter a heading",
    });
    return false;
  }

  if (values.customHeading.length > CUSTOM_HEADING_MAX_LENGTH) {
    setError("customHeading", {
      message: `Heading must be ${CUSTOM_HEADING_MAX_LENGTH} characters or less`,
    });
    return false;
  }

  if (values.customFields === CustomFieldType.TEXT_AREA) {
    if (values.customPlaceholder.length > CUSTOM_PLACEHOLDER_MAX_LENGTH) {
      setError("customPlaceholder", {
        message: `Placeholder text must be ${CUSTOM_PLACEHOLDER_MAX_LENGTH} characters or less`,
      });
      return false;
    }
  }

  if (values.customFields === CustomFieldType.DROPDOWN) {
    if (values.dropdownOptions.length > DROPDOWN_OPTIONS_MAX_LENGTH) {
      setError("dropdownOptions", {
        message: `Dropdown options must be ${DROPDOWN_OPTIONS_MAX_LENGTH} characters or less`,
      });
      return false;
    }

    const dropdownOptions = commaSeparatedValuesFromString(
      values.dropdownOptions
    )
      .map((option) => option.trim())
      .filter((option) => option);

    if (dropdownOptions.length === 0) {
      setError("dropdownOptions", {
        message: "Please add dropdown options",
      });
      return false;
    }

    if (dropdownOptions.length < 2) {
      setError("dropdownOptions", {
        message: "Please add at least two options",
      });
      return false;
    }
  }

  return true;
}

function filterDefaultValues(values: DonationFlowCustomizationFormData) {
  const filteredValues = Object.fromEntries(
    Object.entries(values).filter(([key, value]) => {
      if (key === "paymentOptions") {
        // Empty paymentOptions is the same as all payment options
        const reference: string[] = Object.values(DonationFlowPaymentOption);
        const actual: string[] = value
          .split(",")
          .filter((v: string) => Boolean(v));
        return (
          Boolean(actual.length) &&
          !reference.every((value) => actual.includes(value))
        );
      }
      return (
        value !==
        CONFIGURE_BUTTON_FORM_DEFAULT_VALUES[key as keyof typeof values]
      );
    })
  );
  return filteredValues as Partial<DonationFlowCustomizationFormData>;
}

export function constructDonateUrl(
  values: DonationFlowCustomizationFormData,
  nonprofit: ContextNonprofit
) {
  const filteredValues = filterDefaultValues(values);
  const query = removeUndefinedOrNullValues({
    [DonateModalUrlParams.REQUIRE_SHARE_INFO]:
      filteredValues.donorContactInfo === "required" ? "true" : undefined,
    [DonateModalUrlParams.AMOUNT]:
      values.amountRadio === "fixed" ? filteredValues.amount : undefined,
    [DonateModalUrlParams.SUGGESTED_AMOUNTS]:
      values.amountRadio === "can-choose" && filteredValues.suggestedAmounts
        ? commaSeparatedValuesToString(
            commaSeparatedValuesFromString(
              filteredValues.suggestedAmounts
            ).filter((amount) => amount)
          )
        : undefined,
    [DonateModalUrlParams.FREQUENCY]:
      values.frequency === "can-choose" ? undefined : filteredValues.frequency,
    [DonateModalUrlParams.THEME_COLOR]: filteredValues.themeColor
      ? filteredValues.themeColor.startsWith("#")
        ? filteredValues.themeColor.substring(1)
        : filteredValues.themeColor
      : undefined,
    [DonateModalUrlParams.METHOD]: filteredValues.paymentOptions,
    [DonateModalUrlParams.DESIGNATION]: filteredValues.designation,
    [DonateModalUrlParams.MIN_VALUE]:
      values.amountRadio === "can-choose" && filteredValues.minAmount
        ? filteredValues.minAmount
        : undefined,
    [UTM_QUERY_PARAM.utm_campaign]: "donate-link",
  });

  return (
    "https://www.every.org" +
    getDonateRoutePath({
      nonprofitSlug: nonprofit.primarySlug,
      format: URLFormat.RELATIVE,
      query,
    })
  );
}

export function constructDonateUrlFromCustomization(
  customization: DonationFlowCustomizationData,
  nonprofit: ContextNonprofit
) {
  return constructDonateUrl(
    customizationDataToFormData(customization),
    nonprofit
  );
}

export const getFormValuesFromUrl = (
  search: string
): Partial<DonationFlowCustomizationFormData> => {
  const searchParams = new URLSearchParams(search);
  const paymentOptions = searchParams.get("paymentOptions") ?? undefined;
  const suggestedAmounts = searchParams.get("suggestedAmounts") ?? undefined;
  const amountRadio = searchParams.get("amountRadio") ?? undefined;
  const amount = searchParams.get("amount") ?? undefined;
  const frequency = searchParams.get("frequency") ?? undefined;
  const minAmount = searchParams.get("minAmount") ?? undefined;
  const themeColor = searchParams.get("themeColor") ?? undefined;
  const designation = searchParams.get("designation") ?? undefined;
  const donorContactInfo = searchParams.get("donorContactInfo") ?? undefined;
  const address = searchParams.get("address") ?? undefined;
  const addressRequired = searchParams.get("addressRequired") ?? undefined;
  const customFields = searchParams.get("customFields") ?? undefined;
  const customHeading = searchParams.get("customHeading") ?? undefined;
  const customPlaceholder = searchParams.get("customPlaceholder") ?? undefined;
  const customFieldRequired =
    searchParams.get("customFieldRequired") ?? undefined;
  const dropdownOptions = searchParams.get("dropdownOptions") ?? undefined;

  const validFrequency = isCustomizationFrequencyOption(frequency)
    ? frequency
    : undefined;

  const validDonorContactInfo =
    donorContactInfo === "required" || donorContactInfo === "optional"
      ? donorContactInfo
      : undefined;

  const validAmountRadio =
    amountRadio === "fixed" || amountRadio === "can-choose"
      ? amountRadio
      : undefined;

  const validAddress =
    address === "hide" || address === "show" ? address : undefined;

  const validAddressRequired =
    addressRequired === "optional" || addressRequired === "required"
      ? addressRequired
      : undefined;

  const validCustomFields =
    customFields === null ||
    customFields === CustomFieldType.TEXT_AREA ||
    customFields === CustomFieldType.DROPDOWN
      ? customFields
      : undefined;

  const validCustomFieldRequired =
    customFieldRequired === "optional" || customFieldRequired === "required"
      ? customFieldRequired
      : undefined;

  const values: Partial<DonationFlowCustomizationFormData> = {
    paymentOptions,
    suggestedAmounts,
    amountRadio: validAmountRadio,
    amount,
    frequency: validFrequency,
    minAmount,
    themeColor,
    designation,
    donorContactInfo: validDonorContactInfo,
    address: validAddress,
    addressRequired: validAddressRequired,
    customFields: validCustomFields,
    customHeading,
    customPlaceholder,
    customFieldRequired: validCustomFieldRequired,
    dropdownOptions,
  };

  return removeUndefinedOrNullValues(values);
};

export function customizationDataToFormData(
  customization: DonationFlowCustomizationData
): DonationFlowCustomizationFormData {
  const field = customization.fields?.[0];
  const addressRequired =
    customization.address ??
    CONFIGURE_BUTTON_FORM_DEFAULT_VALUES.addressRequired;
  const donorContactInfo =
    addressRequired === "required"
      ? "required"
      : customization.donorContactInfo ??
        CONFIGURE_BUTTON_FORM_DEFAULT_VALUES.donorContactInfo;

  return {
    ...CONFIGURE_BUTTON_FORM_DEFAULT_VALUES,
    paymentOptions: customization.paymentMethods?.length
      ? customization.paymentMethods.join(",")
      : CONFIGURE_BUTTON_FORM_DEFAULT_VALUES.paymentOptions,
    amountRadio: customization.fixedAmount ? "fixed" : "can-choose",
    amount: customization.fixedAmount ? String(customization.fixedAmount) : "",
    frequency:
      customization.frequency ?? CONFIGURE_BUTTON_FORM_DEFAULT_VALUES.frequency,
    minAmount: customization.minAmount ? String(customization.minAmount) : "",
    suggestedAmounts: customization.suggestedAmounts?.length
      ? customization.suggestedAmounts.join(",")
      : CONFIGURE_BUTTON_FORM_DEFAULT_VALUES.suggestedAmounts,
    address: customization.address === null ? "hide" : "show",
    addressRequired,
    customFields: field?.type ?? null,
    customHeading: field?.heading ?? "",
    customPlaceholder:
      field?.type === CustomFieldType.TEXT_AREA ? field.placeholder ?? "" : "",
    customFieldRequired: field
      ? field.required
        ? "required"
        : "optional"
      : CONFIGURE_BUTTON_FORM_DEFAULT_VALUES.customFieldRequired,
    dropdownOptions:
      field?.type === CustomFieldType.DROPDOWN
        ? field.options?.join(",") ?? ""
        : "",
    themeColor: customization.themeColor ?? "",
    designation: customization.designation ?? "",
    donorContactInfo,
  };
}

export function formDataToCustomizationData(
  values: DonationFlowCustomizationFormData
): DonationFlowCustomizationData {
  const fixedAmount =
    values.amountRadio === "fixed" && values.amount
      ? Number(values.amount)
      : undefined;
  const minAmount =
    values.amountRadio === "can-choose" && values.minAmount
      ? Number(values.minAmount)
      : undefined;
  const fields = values.customFields
    ? [
        {
          type: values.customFields,
          heading: values.customHeading,
          required: values.customFieldRequired === "required",
          ...(values.customFields === CustomFieldType.TEXT_AREA
            ? { placeholder: values.customPlaceholder }
            : {
                options: commaSeparatedValuesFromString(values.dropdownOptions)
                  .map((option) => option.trim())
                  .filter((option) => option),
              }),
        },
      ]
    : undefined;
  const paymentMethods = commaSeparatedValuesFromString(values.paymentOptions)
    .map((method) => method.trim())
    .filter((method): method is DonationFlowPaymentOption =>
      Object.values(DonationFlowPaymentOption).includes(
        method as DonationFlowPaymentOption
      )
    );
  const donorContactInfo =
    values.address === "show" && values.addressRequired === "required"
      ? "required"
      : values.donorContactInfo;

  const customizationData = removeUndefinedOrNullValues({
    paymentMethods,
    fields,
    themeColor: values.themeColor || undefined,
    designation: values.designation || undefined,
    donorContactInfo,
    fixedAmount:
      fixedAmount !== undefined && safeIntCodec.is(fixedAmount)
        ? fixedAmount
        : undefined,
    frequency: values.frequency === "can-choose" ? undefined : values.frequency,
    minAmount:
      minAmount !== undefined && safeIntCodec.is(minAmount)
        ? minAmount
        : undefined,
    suggestedAmounts:
      values.amountRadio === "can-choose"
        ? commaSeparatedValuesFromString(values.suggestedAmounts)
            .map((amount) => Number(amount.trim()))
            .filter(safeIntCodec.is)
        : undefined,
  });

  if (values.address === "hide") {
    return {
      ...customizationData,
      address: null,
    };
  }

  return {
    ...customizationData,
    address: values.addressRequired,
  };
}

export function stripCustomizationDefaults(
  customizationData: DonationFlowCustomizationData
): DonationFlowCustomizationData {
  return Object.fromEntries(
    Object.entries(customizationData).filter(([key, value]) => {
      const defaultValue =
        DEFAULT_CUSTOMIZATION_DATA[
          key as keyof typeof DEFAULT_CUSTOMIZATION_DATA
        ];

      if (key === "paymentMethods") {
        // Payment methods could be in any order, so we check length instead of direct equality.
        // If all default methods are selected in any order, treat it as default.
        const paymentMethodsLength = Array.isArray(value) ? value.length : 0;
        const defaultPaymentMethodsLength = Array.isArray(defaultValue)
          ? defaultValue.length
          : 0;

        return paymentMethodsLength !== defaultPaymentMethodsLength;
      }

      return JSON.stringify(value) !== JSON.stringify(defaultValue);
    })
  ) as DonationFlowCustomizationData;
}

export function isPartialSubset<T>(a: T[], b: T[]) {
  return a.some((value) => b.includes(value));
}

export function getAddressFieldValue({
  address,
  addressRequired,
}: {
  address: DonationFlowCustomizationFormData["address"];
  addressRequired: DonationFlowCustomizationFormData["addressRequired"];
}): AddressFieldValue {
  return address === "hide" ? "hide" : addressRequired ?? "optional";
}
